import React, { useState, useEffect } from 'react';
import '../../../styles/CampaignWizard.css';
import { FiPlus, FiTrash2, FiClock, FiChevronDown, FiChevronRight, FiFileText, FiUser } from 'react-icons/fi';

const SequenceBuilder = ({ sequences = [], templates = [], onChange }) => {
  const [activeSequence, setActiveSequence] = useState(0);
  const [showTemplateList, setShowTemplateList] = useState(false);
  const [emailContent, setEmailContent] = useState('');
  
  // Update email content when active sequence changes
  useEffect(() => {
    if (sequences[activeSequence]) {
      setEmailContent(sequences[activeSequence].content || '');
    }
  }, [activeSequence, sequences]);
  
  // Handle email content change
  const handleContentChange = (e) => {
    const content = e.target.value;
    setEmailContent(content);
    
    // Update the sequence content
    const updatedSequences = [...sequences];
    updatedSequences[activeSequence] = {
      ...updatedSequences[activeSequence],
      content
    };
    
    onChange(updatedSequences);
  };
  
  // Add new sequence
  const addSequence = () => {
    const newOrder = sequences.length + 1;
    const newSequence = {
      subject: '',
      content: '',
      templateId: '',
      order: newOrder,
      delayDays: 2,
      delayHours: 0
    };
    
    const updatedSequences = [...sequences, newSequence];
    onChange(updatedSequences);
    setActiveSequence(updatedSequences.length - 1);
  };
  
  // Remove sequence
  const removeSequence = (index) => {
    if (sequences.length <= 1) {
      alert('You must have at least one sequence email.');
      return;
    }
    
    const updatedSequences = sequences.filter((_, i) => i !== index);
    onChange(updatedSequences);
    
    // Adjust active sequence if necessary
    if (activeSequence >= updatedSequences.length) {
      setActiveSequence(updatedSequences.length - 1);
    }
  };
  
  // Handle field changes
  const handleFieldChange = (field, value) => {
    const updatedSequences = [...sequences];
    updatedSequences[activeSequence] = {
      ...updatedSequences[activeSequence],
      [field]: value
    };
    
    onChange(updatedSequences);
  };
  
  // Apply template
  const applyTemplate = (template) => {
    const updatedSequences = [...sequences];
    updatedSequences[activeSequence] = {
      ...updatedSequences[activeSequence],
      subject: template.subject,
      content: template.content,
      templateId: template._id
    };
    
    onChange(updatedSequences);
    setShowTemplateList(false);
    setEmailContent(template.content || '');
  };
  
  // Personalization variables
  const personalizationVariables = [
    { variable: '{{firstName}}', description: "Recipient's first name" },
    { variable: '{{lastName}}', description: "Recipient's last name" },
    { variable: '{{fullName}}', description: "Recipient's full name" },
    { variable: '{{company}}', description: "Recipient's company" },
    { variable: '{{email}}', description: "Recipient's email address" },
    { variable: '{{position}}', description: "Recipient's job position" },
    { variable: '{{city}}', description: "Recipient's city" },
    { variable: '{{phone}}', description: "Recipient's phone number" },
    { variable: '{{currentDate}}', description: "Current date" },
    { variable: '{{senderName}}', description: "Your name" },
    { variable: '{{senderTitle}}', description: "Your job title" },
    { variable: '{{senderEmail}}', description: "Your email address" },
    { variable: '{{senderPhone}}', description: "Your phone number" },
  ];
  
  // Insert personalization variable into editor
  const insertVariable = (variable) => {
    const textarea = document.getElementById('email-content');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    
    const newContent = before + variable + after;
    setEmailContent(newContent);
    
    // Update the sequence
    const updatedSequences = [...sequences];
    updatedSequences[activeSequence] = {
      ...updatedSequences[activeSequence],
      content: newContent
    };
    
    onChange(updatedSequences);
    
    // Focus the textarea again and set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };
  
  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <h2>Email Sequence</h2>
        <p>Create the sequence of emails for your campaign.</p>
      </div>
      
      <div className="sequence-builder-container">
        <div className="sequence-timeline">
          <div className="timeline-header">
            <h3>Email Sequence</h3>
            <button className="btn btn-sm btn-outline" onClick={addSequence}>
              <FiPlus className="btn-icon" />
              Add Email
            </button>
          </div>
          
          <div className="timeline-items">
            {sequences.map((sequence, idx) => (
              <div 
                key={idx} 
                className={`timeline-item ${activeSequence === idx ? 'active' : ''}`}
                onClick={() => setActiveSequence(idx)}
              >
                <div className="timeline-number">{idx + 1}</div>
                <div className="timeline-content">
                  <div className="timeline-title">
                    {sequence.subject || `Email ${idx + 1}`}
                  </div>
                  <div className="timeline-delay">
                    <FiClock className="icon" />
                    {idx === 0 ? (
                      'Initial Email'
                    ) : (
                      `${sequence.delayDays} day${sequence.delayDays !== 1 ? 's' : ''} ${sequence.delayHours > 0 ? `${sequence.delayHours} hour${sequence.delayHours !== 1 ? 's' : ''}` : ''} after previous`
                    )}
                  </div>
                </div>
                <div className="timeline-actions">
                  <button 
                    className="btn-icon-only" 
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSequence(idx);
                    }}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="sequence-editor">
          <div className="editor-header">
            <h3>Edit Email {activeSequence + 1}</h3>
            
            <div className="editor-template-selector">
              <button 
                className="btn btn-sm btn-outline" 
                onClick={() => setShowTemplateList(!showTemplateList)}
              >
                <FiFileText className="btn-icon" />
                Use Template
                <FiChevronDown className={`dropdown-arrow ${showTemplateList ? 'open' : ''}`} />
              </button>
              
              {showTemplateList && (
                <div className="template-dropdown">
                  <div className="template-dropdown-header">
                    <h4>Select a Template</h4>
                  </div>
                  
                  <div className="template-list">
                    {templates.length > 0 ? (
                      templates.map(template => (
                        <div 
                          key={template._id} 
                          className="template-item"
                          onClick={() => applyTemplate(template)}
                        >
                          <div className="template-name">{template.name}</div>
                          <div className="template-subject">{template.subject}</div>
                          <FiChevronRight className="template-icon" />
                        </div>
                      ))
                    ) : (
                      <div className="empty-templates">
                        <p>No templates available</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {activeSequence > 0 && (
            <div className="sequence-delay-settings">
              <h4>Delay Settings</h4>
              <p>Set how long to wait after the previous email before sending this one.</p>
              
              <div className="delay-inputs">
                <div className="delay-input-group">
                  <label>Days</label>
                  <input
                    type="number"
                    min="0"
                    value={sequences[activeSequence].delayDays}
                    onChange={(e) => handleFieldChange('delayDays', parseInt(e.target.value) || 0)}
                  />
                </div>
                
                <div className="delay-input-group">
                  <label>Hours</label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={sequences[activeSequence].delayHours}
                    onChange={(e) => handleFieldChange('delayHours', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="subject">Email Subject</label>
            <input
              type="text"
              id="subject"
              value={sequences[activeSequence]?.subject || ''}
              onChange={(e) => handleFieldChange('subject', e.target.value)}
              placeholder="Enter email subject..."
            />
          </div>
          
          <div className="form-group">
            <label>Email Content</label>
            <div className="email-editor">
              <textarea
                id="email-content"
                value={emailContent}
                onChange={handleContentChange}
                rows="10"
                placeholder="Enter your email content here..."
              ></textarea>
            </div>
          </div>
          
          <div className="personalization-variables">
            <div className="variables-header">
              <h4>
                <FiUser className="icon" />
                Personalization Variables
              </h4>
              <p>Click on a variable to insert it into your email.</p>
            </div>
            
            <div className="variables-list">
              {personalizationVariables.map((item, idx) => (
                <div 
                  key={idx} 
                  className="variable-item"
                  onClick={() => insertVariable(item.variable)}
                >
                  <div className="variable-code">{item.variable}</div>
                  <div className="variable-description">{item.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SequenceBuilder;